import { ADD_STEP_VISIBLE, STEP_CATEGORIES, type StepCategory, type WorkflowTrigger } from "@sagot/constants";
import type { PathDef, Step } from "../../types";
import type { ServiceDeskState } from "../../service/use-service-desk";
import type { DefaultWorkflowInfo } from "../system/default-context";
import { buildCatalog, searchTermsOf, type AddStepEntry, type Translate } from "./add-step-catalog";
import { searchScore, searchTokens } from "./add-step-search";
import { isTerminalStep } from "./step-exits";
import { suggestedEntries } from "./suggestions";

export type SectionId = "replace" | "suggested" | StepCategory;

export interface AddStepSectionModel {
  id: SectionId;
  /** Listed whenever the section is open. */
  visible: AddStepEntry[];
  /** Listed after "View more". Always empty while a search is typed: a search shows every match. */
  rest: AddStepEntry[];
}

/** The last step of the path when nothing can follow it; the menu offers to replace it. */
export interface ReplaceTarget {
  step: Step;
  label: string;
}

export interface AddStepModel {
  replace: ReplaceTarget | null;
  searching: boolean;
  sections: AddStepSectionModel[];
}

export interface AddStepInput {
  trigger: WorkflowTrigger;
  /** The workspace plan; unknown (still loading) never locks a step. */
  plan: string | undefined;
  pathId: string;
  path: PathDef;
  query: string;
  t: Translate;
  /** Set when the workflow is one of the defaults. */
  defaults?: DefaultWorkflowInfo | null;
  /** Whether the Service Desk can be used here (locks its steps when not). */
  serviceDesk?: ServiceDeskState;
}

function split(id: SectionId, entries: AddStepEntry[]): AddStepSectionModel {
  return { id, visible: entries.slice(0, ADD_STEP_VISIBLE), rest: entries.slice(ADD_STEP_VISIBLE) };
}

/** Steps the author can do instead of the last one: everything but the step itself, those that can be added first. */
function replacements(catalog: readonly AddStepEntry[], last: Step): AddStepEntry[] {
  const others = catalog.filter((entry) => entry.type !== last.type);
  return [...others.filter((entry) => !entry.locked), ...others.filter((entry) => entry.locked)];
}

/** The entries that answer the search, best first (the menu order breaks ties). */
function matching(entries: readonly AddStepEntry[], tokens: readonly string[], t: Translate): AddStepEntry[] {
  return entries
    .map((entry, index) => ({ entry, index, score: searchScore(tokens, searchTermsOf(entry, t)) }))
    .flatMap((hit) => (hit.score === null ? [] : [{ entry: hit.entry, index: hit.index, score: hit.score }]))
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((hit) => hit.entry);
}

function searchSections(pool: readonly AddStepEntry[], tokens: readonly string[], t: Translate, replacing: boolean): AddStepSectionModel[] {
  if (replacing) {
    const found = matching(pool, tokens, t);
    return found.length > 0 ? [{ id: "replace", visible: found, rest: [] }] : [];
  }
  return STEP_CATEGORIES.flatMap((category) => {
    const found = matching(pool.filter((entry) => entry.category === category), tokens, t);
    return found.length > 0 ? [{ id: category, visible: found, rest: [] }] : [];
  });
}

/**
 * What the Add-step menu shows for one path: "Replace with" when the last step ends the path, otherwise "Suggested next
 * steps" and the three lists. Pure: the same trigger, plan, path and search always give the same menu. The trigger and plan
 * gates are the entries' `locked` flags; a search finds locked steps too, so every step can be reached.
 */
export function buildAddStepModel({ trigger, plan, pathId, path, query, t, defaults = null, serviceDesk }: AddStepInput): AddStepModel {
  const catalog = buildCatalog({ trigger, plan, t, defaults, serviceDesk });
  const tokens = searchTokens(query);
  const searching = tokens.length > 0;
  const last = path.steps.at(-1);
  const replace: ReplaceTarget | null = last && isTerminalStep(last) ? { step: last, label: t(`engage.workflows.steps.${last.type}`) } : null;
  const pool = replace ? replacements(catalog, replace.step) : catalog;
  if (searching) return { replace, searching, sections: searchSections(pool, tokens, t, replace !== null) };
  if (replace) return { replace, searching, sections: [split("replace", pool)] };
  const suggested = suggestedEntries({ trigger, pathId, steps: path.steps }, catalog);
  const lists = STEP_CATEGORIES.map((category) => split(category, catalog.filter((entry) => entry.category === category)));
  return { replace, searching, sections: [...(suggested.length > 0 ? [{ id: "suggested" as const, visible: suggested, rest: [] }] : []), ...lists] };
}
